/**
 * PlaceSummarySection — shared editorial section for place summary (description_short).
 * Canonical owner: Editor portal. Reused by Admin portal.
 */
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { EditorialAiAssistant } from "@/components/ai/EditorialAiAssistant";
import { cn } from "@/lib/utils";
import type { UseFormReturn } from "react-hook-form";

interface PlaceSummarySectionProps {
  form: UseFormReturn<any>;
  placeName: string;
  municipality?: string | null;
  landskap?: string | null;
  tags?: string[];
  seasons?: string[];
  intentLabels?: string[];
  disabled?: boolean;
  /** Hide AI assistant (e.g. for simplified Admin views) */
  hideAi?: boolean;
}

export function PlaceSummarySection({
  form,
  placeName,
  municipality,
  landskap,
  tags,
  seasons,
  intentLabels,
  disabled = false,
  hideAi = false,
}: PlaceSummarySectionProps) {
  return (
    <FormField
      control={form.control}
      name="description_short"
      render={({ field }) => {
        const charLen = (field.value || "").length;
        const hasText = charLen > 0;

        const charLabel =
          charLen === 0
            ? "Ingen text ännu"
            : charLen < 120
              ? `${charLen} tecken — lite kort`
              : `${charLen} tecken`;

        return (
          <FormItem>
            <div className="flex items-center justify-between gap-2">
              <FormLabel className="text-xs">Kort sammanfattning</FormLabel>
              {!hideAi && (
                <EditorialAiAssistant
                  task="place_summary_suggest"
                  lengthProfile="summary"
                  currentText={field.value || undefined}
                  onAccept={(text) =>
                    form.setValue("description_short", text, { shouldDirty: true })
                  }
                  buildContext={() => ({
                    name: placeName || "Platsen",
                    municipality: municipality || undefined,
                    landskap: landskap || undefined,
                    tags: tags?.slice(0, 6),
                    seasons,
                    intentLabels,
                    currentText: field.value || undefined,
                  })}
                  disabled={disabled || !placeName?.trim()}
                  allowRewrite
                  hasExistingText={hasText}
                  showAdvanced
                />
              )}
            </div>
            <FormControl>
              <Textarea
                placeholder="2–3 meningar om platsen."
                {...field}
                className="min-h-[72px] resize-none"
              />
            </FormControl>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[11px] text-muted-foreground/60">
                Visas i listkort och sök
              </span>
              <span
                className={cn(
                  "text-[11px]",
                  charLen < 120
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-emerald-600 dark:text-emerald-400"
                )}
              >
                {charLabel}
              </span>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
